/**
 * Reading stored placement inventories.
 *
 * Paths are RELATIVE, unlike the rest of the app's api.ts which builds absolute URLs from
 * VITE_API_BASE_URL. Vite proxies /api to the backend, so a relative path works both at
 * localhost:5137 and over the LAN at 192.168.x.x:5137 — and demoing from a phone or a second
 * laptop is exactly when an absolute http://localhost:8000 breaks.
 *
 * These endpoints require a session. The backend uses a cookie, so credentials are included
 * explicitly rather than relying on the same-origin default holding if this ever moves
 * cross-origin.
 */
import type { RunListEntry, StoredPlacementRun } from "@/types/placement-run";

const BASE = "/api/v1/placements";

/** Thrown with the HTTP status kept, because the caller renders 401 and 404 differently. */
export class PlacementFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "PlacementFetchError";
  }
}

async function get<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, { credentials: "include" });
  } catch (cause) {
    // A network-level failure is not a server answer; 0 marks "never reached it".
    throw new PlacementFetchError(
      cause instanceof Error ? cause.message : "Could not reach the API",
      0,
    );
  }
  if (!response.ok) {
    // FastAPI puts the reason in `detail`, and here the reasons are the useful part:
    // "no placement inventory yet", "Script not found".
    const body = await response.json().catch(() => null);
    throw new PlacementFetchError(
      body?.detail ?? `${response.status} ${response.statusText}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** The most recently computed analysis of one script. */
export const fetchLatestInventory = (scriptId: string) =>
  get<StoredPlacementRun>(`/scripts/${encodeURIComponent(scriptId)}`);

/** Every analysis of one script, newest first, without the rows. */
export const fetchRunHistory = (scriptId: string) =>
  get<{ script_id: string; count: number; runs: RunListEntry[] }>(
    `/scripts/${encodeURIComponent(scriptId)}/runs`,
  );

/** One specific analysis, by hash. Used to look at an earlier prompt's output. */
export const fetchRunByHash = (runHash: string) =>
  get<StoredPlacementRun>(`/runs/${encodeURIComponent(runHash)}`);
